import React, { useState, useEffect, useCallback } from "react";
import { Offcanvas, OffcanvasBody, Button } from "reactstrap";
import { X, Tag, User } from "lucide-react";
import SearchDropdown from "../searchDropdown";
import { getDeliveryUsers } from "../../api/user";
import { updatePackage } from "../../api/packages";
import { showToast } from "../../utils/showToast";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import { packageStatus } from "../../utils/packageStatus";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import "./editOrderDrawer.scss";

// Estados editables (igual que el antiguo "Cambiar estado").
// OJO: no incluir 100 ("Etiqueta impresa"): no existe en el StatusBeartrackEnum
// del backend (0,1,2,3,30,4,5,6,7,21,22,31) y PUT packages/update/:id lo rechaza
// con un 400. Ver STATUS_CLASS, que sí lo mantiene para pintar datos viejos.
const STATUS_LIST = [
  { label: "Pendiente", value: 0 },
  { label: "Recogido", value: 1 },
  { label: "En bodega", value: 2 },
  { label: "En ruta", value: 3 },
  { label: "Entregado", value: 4 },
  { label: "Cancelado", value: 5 },
  { label: "Dirección incorrecta", value: 6 },
  { label: "Sin moradores", value: 7 },
];

// Mapa estado → clase del pill (misma que el listado de Pedidos).
const STATUS_CLASS = { 0: "pending", 1: "picked", 2: "picked", 3: "route", 4: "delivered", 5: "cancelled", 6: "failed", 7: "failed", 30: "route", 100: "storage" };

const resolveAssign = (assignTo) =>
  assignTo?._id || (typeof assignTo === "string" ? assignTo : null);

/**
 * Drawer lateral para editar un pedido: combina "Asignar repartidor" y
 * "Cambiar estado" en un solo panel deslizante (Offcanvas derecha). Mantiene la
 * lógica de updatePackage; solo envía los campos que cambiaron.
 */
export default function EditOrderDrawer({ isOpen, closeModal, data, updateData }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const logisticCompanyId = user?.logisticCompany?._id || user?.logisticCompany || null;

  const [deliveryList, setDeliveryList] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [statusVal, setStatusVal] = useState(null);
  const [assignVal, setAssignVal] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback(() => closeModal(false), [closeModal]);

  const originalStatus = typeof data?.status === "number" ? data.status : null;
  const originalAssign = resolveAssign(data?.assignTo);

  // Inicializar con los valores actuales del pedido al abrir.
  useEffect(() => {
    if (!isOpen) return;
    setStatusVal(originalStatus);
    setAssignVal(originalAssign);
  }, [isOpen, originalStatus, originalAssign]);

  // Cargar repartidores activos al abrir.
  useEffect(() => {
    if (!isOpen || !logisticCompanyId) return;
    let cancelled = false;
    setLoadingDrivers(true);
    getDeliveryUsers("active", "delivery", logisticCompanyId)
      .then((result) => {
        if (cancelled) return;
        const users = result?.docs || result?.data?.docs || (Array.isArray(result) ? result : []);
        setDeliveryList(
          (Array.isArray(users) ? users : [])
            .filter((u) => u && u._id)
            .map((u) => ({
              label: `${u.name || ""} ${u.lastName || ""}`.trim() || u.email || t("Sin nombre"),
              value: u._id,
            }))
        );
      })
      .catch(() => { if (!cancelled) setDeliveryList([]); })
      .finally(() => { if (!cancelled) setLoadingDrivers(false); });
    return () => { cancelled = true; };
  }, [isOpen, logisticCompanyId]);

  const statusChanged = statusVal != null && statusVal !== originalStatus;
  const assignChanged = !!assignVal && assignVal !== originalAssign;
  const hasChanges = statusChanged || assignChanged;

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      showToast(t("No hay cambios para guardar."), false);
      return;
    }
    setSubmitting(true);
    const values = {};
    if (statusChanged) values.status = statusVal;
    if (assignChanged) values.assignTo = assignVal;
    try {
      const res = await updatePackage({ id: data?._id, values });
      if (res?.error) {
        // updatePackage ya no tostea: el texto del backend se muestra acá, una vez.
        showToast(res.error?.message || t("Error al actualizar el pedido"), false);
      } else {
        showToast(t("Pedido actualizado correctamente."), true);
        close();
        updateData(true);
      }
    } catch (e) {
      showToast(apiErrorMessage(e, t("Error al actualizar el pedido")), false);
    } finally {
      setSubmitting(false);
    }
  }, [hasChanges, statusChanged, assignChanged, statusVal, assignVal, data, close, updateData]);

  const correlative = data?.followingNumber || data?.correlative?.comparative || data?.correlative || "—";
  const company = data?.client?.name || data?.company?.name || data?.companyName || "";
  const receiver = data?.receiver?.name || data?.receiverName || "";
  const curClass = STATUS_CLASS[originalStatus] || "pending";

  return (
    <Offcanvas isOpen={isOpen} toggle={close} direction="end" className="eo-drawer">
      <div className="eo-drawer__head">
        <h2 className="eo-drawer__title">{t("Editar pedido")}</h2>
        <button type="button" className="eo-drawer__close" onClick={close} aria-label={t("Cerrar")}>
          <X size={20} />
        </button>
      </div>

      <OffcanvasBody className="eo-drawer__body">
        <div className="eo-form">
          <div className="eo-form__fields">
            {/* Resumen del pedido */}
            <div className="eo-summary">
              <div className="eo-summary__row">
                <span className="eo-summary__correlative">#{correlative}</span>
                {originalStatus != null && (
                  <span className={`status-badge status-badge--${curClass}`}>
                    {packageStatus[originalStatus]}
                  </span>
                )}
              </div>
              {(company || receiver) && (
                <div className="eo-summary__sub">
                  {company}
                  {company && receiver ? " · " : ""}
                  {receiver}
                </div>
              )}
            </div>

            {/* Estado */}
            <div className="eo-group">
              <label className="eo-label"><Tag size={14} /> {t("Estado")}</label>
              <SearchDropdown
                options={STATUS_LIST}
                selected={STATUS_LIST.find((s) => s.value === statusVal)?.label}
                placeholder={t("Selecciona el estado")}
                onChange={setStatusVal}
              />
            </div>

            {/* Repartidor */}
            <div className="eo-group">
              <label className="eo-label"><User size={14} /> {t("Repartidor")}</label>
              <SearchDropdown
                options={deliveryList}
                selected={deliveryList.find((d) => d.value === assignVal)?.label}
                placeholder={
                  loadingDrivers
                    ? t("Cargando repartidores...")
                    : deliveryList.length === 0
                    ? t("No hay repartidores activos")
                    : t("Asignar repartidor")
                }
                onChange={setAssignVal}
                disabled={loadingDrivers || deliveryList.length === 0}
              />
              <span className="eo-hint">{t("Solo repartidores activos de tu empresa.")}</span>
            </div>
          </div>

          <div className="eo-form__footer">
            <Button type="button" className="eo-btn eo-btn--ghost" onClick={close} disabled={submitting}>
              {t("Cancelar")}
            </Button>
            <Button
              type="button"
              className="eo-btn eo-btn--primary"
              onClick={handleSave}
              disabled={submitting || !hasChanges}
            >
              {submitting ? t("Guardando…") : t("Guardar cambios")}
            </Button>
          </div>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
}
